<template>
  <div class="power-user-allocation">
    <mainmenu selected="power-user-allocation"></mainmenu>

    <div class="allocation-shell">
      <div class="allocation-toolbar">
        <div>
          <h1>{{ $t("power_user_allocation.page_title") }}</h1>
          <p>{{ $t("power_user_allocation.subtitle") }}</p>
        </div>
        <el-button type="primary" icon="el-icon-refresh" @click="refresh_info">{{ $t("power_user_allocation.refresh") }}</el-button>
      </div>

      <div class="summary-strip">
        <div class="summary-item">
          <strong>{{ Totals.Countries || 0 }}</strong>
          <span>{{ $t("power_user_allocation.countries") }}</span>
        </div>
        <div class="summary-item">
          <strong>{{ Totals.Factories || 0 }}</strong>
          <span>{{ $t("power_user_allocation.factories") }}</span>
        </div>
        <div class="summary-item">
          <strong>{{ Totals.PowerUsers || 0 }}</strong>
          <span>{{ $t("power_user_allocation.power_users") }}</span>
        </div>
        <div class="summary-item" :class="{ warning: (Totals.UncoveredFactories || 0) > 0 }">
          <strong>{{ Totals.UncoveredFactories || 0 }}</strong>
          <span>{{ $t("power_user_allocation.gaps") }}</span>
        </div>
      </div>

      <div class="admins-band" v-if="SystemAdmins.length > 0">
        <div class="band-title">{{ $t("power_user_allocation.system_admins") }}</div>
        <div class="admin-list">
          <span v-for="admin in SystemAdmins" :key="admin.Id">{{ admin.Name || admin.Email }}</span>
        </div>
      </div>

      <div class="allocation-board" v-loading="loading">
        <table class="country-table" v-if="Countries.length > 0">
          <thead>
            <tr>
              <th v-for="country in Countries" :key="country.Country">
                <div class="country-heading">
                  <span>{{ country_label(country.Country) }}</span>
                  <small>{{ country.FactoryCount }} {{ $t("power_user_allocation.factories_short") }}</small>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="factory-row">
              <td v-for="country in Countries" :key="country.Country + '-factories'">
                <div class="cell-label">{{ $t("power_user_allocation.factories") }}</div>
                <div class="factory-chips">
                  <span
                    v-for="factory in country.Factories"
                    :key="country.Country + '-factory-' + factory.Id"
                    class="factory-chip"
                    :class="{ uncovered: !factory.Covered }">
                    {{ factory.ShortCode }}
                  </span>
                </div>
              </td>
            </tr>

            <tr class="power-row">
              <td v-for="country in Countries" :key="country.Country + '-users'">
                <div class="cell-label">{{ $t("power_user_allocation.power_users") }}</div>
                <div class="person-list" v-if="country.PowerUsers.length > 0">
                  <div class="person" v-for="user in country.PowerUsers" :key="country.Country + '-user-' + user.Id">
                    <div class="avatar">{{ initials(user) }}</div>
                    <div class="person-body">
                      <strong>{{ user.Name || user.Email }}</strong>
                      <span>{{ user.Email }}</span>
                      <div class="person-factories">
                        <span
                          v-for="factory in user.Factories"
                          :key="user.Id + '-factory-' + factory.Id"
                          :class="{ primary: factory.IsPrimary }">
                          {{ factory.ShortCode }}<em v-if="factory.IsPrimary"> {{ $t("users.primary_factory") }}</em>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="empty-cell" v-else>{{ $t("power_user_allocation.no_power_users") }}</div>
              </td>
            </tr>

            <tr class="coverage-row">
              <td v-for="country in Countries" :key="country.Country + '-coverage'">
                <div class="cell-label">{{ $t("power_user_allocation.uncovered_plants") }}</div>
                <div class="factory-chips" v-if="country.UncoveredFactories.length > 0">
                  <span
                    v-for="factory in country.UncoveredFactories"
                    :key="country.Country + '-uncovered-' + factory.Id"
                    class="factory-chip uncovered">
                    {{ factory.ShortCode }}
                  </span>
                </div>
                <div class="ok-cell" v-else>{{ $t("power_user_allocation.no_uncovered") }}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="empty-state" v-if="!loading && Countries.length === 0">
          {{ $t("power_user_allocation.empty") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainMenu from "./../widgets/MainMenu";
import settings from "./../backend/LocalSettings";
import { countryLabel } from "./../utils/countries";

export default {
  name: "power-user-allocation",
  components: {
    "mainmenu": MainMenu
  },
  data() {
    return {
      loading: false,
      Countries: [],
      SystemAdmins: [],
      Totals: {}
    };
  },
  methods: {
    refresh_info: function() {
      this.loading = true;
      this.$http
        .post("users/power_user_overview", {})
        .then(function(response) {
          this.loading = false;
          if(settings.verify_response(response, this.$rights)) {
            if(response.body.ErrorMessage) {
              this.$message.error(response.body.ErrorMessage);
              return;
            }
            this.Countries = response.body.Countries || [];
            this.SystemAdmins = response.body.SystemAdmins || [];
            this.Totals = response.body.Totals || {};
          } else {
            this.$router.push("/");
          }
        }, function() {
          this.loading = false;
          this.$message.error(this.$t("power_user_allocation.load_failed"));
        });
    },
    initials: function(user) {
      var label = (user.Name || user.Email || "").trim();
      if(label === "") return "";
      var parts = label.split(/\s+/).filter(function(part) { return part !== ""; });
      if(parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    country_label: function(country) {
      return countryLabel(this, country);
    }
  },
  mounted: function() {
    this.refresh_info();
  }
};
</script>

<style lang="less" scoped>
.allocation-shell {
  min-height: calc(100vh - 56px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px 18px;
}

.allocation-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.14);
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(19, 55, 73, 0.10);

  h1 {
    margin: 0;
    color: #172b38;
    font-size: 30px;
    line-height: 1.1;
    font-weight: 700;
  }

  p {
    margin: 6px 0 0;
    color: #607281;
    font-size: 14px;
  }

  .el-button {
    height: 36px;
    padding: 0 16px;
    border-color: #157c91;
    background: #178da5;
    box-shadow: 0 6px 14px rgba(23, 141, 165, 0.22);
  }
}

.summary-strip,
.admins-band {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.12);
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(19, 55, 73, 0.08);
}

.summary-item {
  min-width: 130px;
  padding: 8px 12px;
  border-left: 3px solid #2196a6;
  background: #f8fbfc;
  border-radius: 6px;

  strong {
    display: block;
    color: #183446;
    font-size: 24px;
    line-height: 1;
  }

  span {
    display: block;
    margin-top: 4px;
    color: #657582;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  &.warning {
    border-left-color: #d9822b;
    background: #fff8ef;

    strong,
    span {
      color: #9a5618;
    }
  }
}

.admins-band {
  align-items: flex-start;
}

.band-title {
  flex: 0 0 auto;
  color: #344955;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  padding-top: 4px;
}

.admin-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  span {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 8px;
    color: #36515f;
    background: #edf6f8;
    border: 1px solid #cfe6ec;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
  }
}

.allocation-board {
  min-height: 420px;
  overflow: auto;
  background: #ffffff;
  border: 1px solid rgba(31, 73, 95, 0.14);
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(19, 55, 73, 0.12);
}

.country-table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    border-right: 1px solid #d9e2e7;
    border-bottom: 1px solid #d9e2e7;
    vertical-align: top;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 0;
    background: #4389cf;
  }

  td {
    padding: 12px;
    background: #ffffff;
  }
}

.country-heading {
  min-height: 82px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  color: #ffffff;
  text-align: center;

  span {
    font-size: 15px;
    font-weight: 800;
  }

  small {
    color: rgba(255, 255, 255, 0.86);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
  }
}

.factory-row td {
  background: #f7fafc;
}

.power-row td {
  min-height: 260px;
}

.coverage-row td {
  background: #fbfcfd;
}

.cell-label {
  margin-bottom: 8px;
  color: #5b6873;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.factory-chips,
.person-factories {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.factory-chip,
.person-factories span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 7px;
  color: #33505d;
  background: #eef7f4;
  border: 1px solid #c7e7dc;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;

  &.uncovered {
    color: #9a5618;
    background: #fff3df;
    border-color: #f1c27d;
  }

  &.primary {
    color: #277130;
    background: #edf8ec;
    border-color: #bfe5bb;
  }

  em {
    margin-left: 3px;
    font-style: normal;
    font-weight: 800;
  }
}

.person-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.person {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 9px;
  background: #f8fbfc;
  border: 1px solid #e1ebef;
  border-radius: 8px;
}

.avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: #218da0;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 800;
}

.person-body {
  min-width: 0;

  strong,
  span {
    display: block;
  }

  strong {
    color: #213947;
    font-size: 13px;
    line-height: 1.25;
  }

  > span {
    margin: 2px 0 7px;
    color: #748391;
    font-size: 11px;
    word-break: break-word;
  }
}

.empty-cell,
.ok-cell,
.empty-state {
  color: #748391;
  font-size: 13px;
  font-weight: 600;
}

.ok-cell {
  color: #3f7f45;
}

.empty-state {
  padding: 48px;
  text-align: center;
}

@media (max-width: 768px) {
  .allocation-shell {
    padding: 10px;
  }

  .allocation-toolbar {
    align-items: stretch;
    flex-direction: column;

    h1 {
      font-size: 26px;
    }
  }

  .summary-strip,
  .admins-band {
    align-items: stretch;
    flex-direction: column;
  }

  .summary-item {
    width: 100%;
  }
}
</style>
